import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { GiPodium } from "react-icons/gi";
import {
  IoBrushOutline,
  IoChatbubbleEllipsesOutline,
  IoChevronDown,
  IoLanguage,
  IoPlay,
} from "react-icons/io5";
import {
  DIRECT_MESSAGE_PANEL_OPEN_EVENT,
  DIRECT_MESSAGE_UNREAD_EVENT,
  DIRECT_MESSAGE_UNREAD_GLOBAL_KEY,
} from "../../services/directMessageUi";
import {
  isGuestMustRegisterError,
  openGuestChampionshipRegister,
} from "../championship/guestChampionshipPrompt";
import PuzzleBoard2D from "../puzzles/PuzzleBoard2D";
import ModernSidebar from "./ModernSidebar";
import {
  MODES,
  MODE_BY_LIVE,
  SELECTED_MODE_KEY,
  buildActivityRows,
  normalizeMode,
  pickWaitingMatches,
  readSelectedMode,
} from "./LobbyModern";
import "./lobbyModern.css";
import "./lobbyLive.css";

// Cuarto diseño del lobby («En vivo», propuesta 4): a la izquierda eliges modo y rival y pulsas
// JUGAR; en el centro, el partido en vivo con su tablero 2D; a la derecha, Campeonato,
// partidas esperando rival y mejores jugadores.

const COPY = {
  es: {
    greeting: (name) => `Hola, ${name}`,
    title: "Jugar",
    nextGame: "Tu próxima partida",
    mode: "Modo",
    rival: "Rival",
    rivalOnline: "En línea",
    rivalBot: "Bot",
    rivalFriends: "Amigos",
    play: "JUGAR",
    playHint: (mode) => `${mode} · rival de tu nivel`,
    playBotHint: "Partida contra el bot",
    playFriendsHint: "En el mismo dispositivo",
    playing: (n) => `${n} jugando`,
    startHere: "empieza aquí",
    create: "Crear",
    find: "Buscar",
    puzzles: "Puzzles",
    sandbox: "Arena",
    learnTitle: "Aprende a jugar",
    learnText: "Tutorial guiado paso a paso en 3 minutos. Después te sugerimos el modo ideal.",
    learnStart: "Empezar tutorial guiado",
    learnMore: "Ver guías en Aprender",
    liveNow: "En vivo ahora",
    liveCount: (n) => `${n} ${n === 1 ? "PARTIDO" : "PARTIDOS"}`,
    seeAll: "Ver todos ›",
    turn: "su turno",
    waitingTurn: "espera",
    turnN: (n) => `Turno ${n}`,
    watchers: (n) => `👁 ${n}`,
    watch: "Mirar este partido",
    next: "Siguiente",
    watchShort: "VER",
    replay: "REPETICIÓN",
    replayTitle: "Nadie juega ahora mismo",
    replayText: "Mira una repetición destacada mientras tanto.",
    replayButton: "Ver repeticiones",
    emptyTitle: "No hay partidos en vivo ahora mismo",
    emptyText: "Cuando alguien juegue, lo verás aquí. ¡Pulsa JUGAR y sé el primero!",
    boardLabel: "Tablero de la partida visto desde arriba",
    championship: "CAMPEONATO XIMA",
    champOpen: (seats) => `Inscripción abierta · ${seats}`,
    champRunning: (seats) => `En curso · ${seats}`,
    champIdle: "Pirámide, horarios y partidas en vivo",
    champJoin: "UNIRME",
    champJoined: "INSCRITO",
    champFull: "LLENO",
    champWatch: "VER CAMPEONATO",
    champJoinedOk: "¡Te uniste al campeonato!",
    champError: "No se pudo completar la acción.",
    waitingRival: "Esperando rival",
    mine: "TU PARTIDA",
    join: "UNIRSE",
    joining: "...",
    private: "PRIVADA",
    noWaiting: "Nadie espera rival ahora.",
    createGame: "Crear juego",
    topPlayers: "Mejores jugadores",
    noRanking: "Sin datos todavía",
    designMode: "Modo Diseñar",
    messages: "Mensajes",
    language: "Idioma",
    modeScience: "Ciencia",
    modeCienzar: "Cienzar",
    modeContrazar: "Contrazar",
    modeChance: "Azar",
    minutes: (n) => `${n} min`,
  },
  en: {
    greeting: (name) => `Hi, ${name}`,
    title: "Play",
    nextGame: "Your next game",
    mode: "Mode",
    rival: "Opponent",
    rivalOnline: "Online",
    rivalBot: "Bot",
    rivalFriends: "Friends",
    play: "PLAY",
    playHint: (mode) => `${mode} · rival at your level`,
    playBotHint: "Game against the bot",
    playFriendsHint: "On the same device",
    playing: (n) => `${n} playing`,
    startHere: "start here",
    create: "Create",
    find: "Find",
    puzzles: "Puzzles",
    sandbox: "Sandbox",
    learnTitle: "Learn to play",
    learnText: "Step-by-step guided tutorial in 3 minutes. Then we suggest the ideal mode for you.",
    learnStart: "Start guided tutorial",
    learnMore: "See guides in Learn",
    liveNow: "Live now",
    liveCount: (n) => `${n} ${n === 1 ? "MATCH" : "MATCHES"}`,
    seeAll: "See all ›",
    turn: "to move",
    waitingTurn: "waiting",
    turnN: (n) => `Turn ${n}`,
    watchers: (n) => `👁 ${n}`,
    watch: "Watch this match",
    next: "Next",
    watchShort: "WATCH",
    replay: "REPLAY",
    replayTitle: "Nobody is playing right now",
    replayText: "Watch a featured replay meanwhile.",
    replayButton: "See replays",
    emptyTitle: "No live matches right now",
    emptyText: "When someone plays, you will see it here. Press PLAY and be the first!",
    boardLabel: "Match board seen from above",
    championship: "XIMA CHAMPIONSHIP",
    champOpen: (seats) => `Registration open · ${seats}`,
    champRunning: (seats) => `In progress · ${seats}`,
    champIdle: "Bracket, schedule and live games",
    champJoin: "JOIN",
    champJoined: "JOINED",
    champFull: "FULL",
    champWatch: "OPEN CHAMPIONSHIP",
    champJoinedOk: "You joined the championship!",
    champError: "The action could not be completed.",
    waitingRival: "Waiting for a rival",
    mine: "YOUR GAME",
    join: "JOIN",
    joining: "...",
    private: "PRIVATE",
    noWaiting: "Nobody is waiting for a rival.",
    createGame: "Create game",
    topPlayers: "Top players",
    noRanking: "No data yet",
    designMode: "Design mode",
    messages: "Messages",
    language: "Language",
    modeScience: "Science",
    modeCienzar: "Cienzar",
    modeContrazar: "Contrazar",
    modeChance: "Chance",
    minutes: (n) => `${n} min`,
  },
};

const initialsOf = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const playerKey = (value) => String(value || "").trim().toLowerCase();

const playerLabel = (player) =>
  String(player?.displayName || player?.name || player?.playerCode || "-").trim() || "-";

const formatClock = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil((Number(ms) || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

// Partidos en vivo ordenados: el más mirado primero.
export const sortLiveMatches = (matches) =>
  (Array.isArray(matches) ? matches : [])
    .filter((match) => match && String(match.type || "live") !== "historical")
    .sort((a, b) => (Number(b.spectators) || 0) - (Number(a.spectators) || 0));

const LobbyLive = ({
  language,
  userName,
  userAvatar,
  userIds,
  rankLabel,
  cimaxTotalLabel,
  isAdmin,
  socialPending,
  badges = {},
  designChooser,
  newsTicker,
  rankingSections,
  rankingLoading,
  onTutorial,
  onPlayMode,
  onJoinMatch,
  onOpenPlayerProfile,
  onOpenProfile,
  onNavigate,
  actions,
}) => {
  const copy = COPY[language] || (language === "es" ? COPY.es : COPY.en);
  const [selectedModeId, setSelectedModeId] = useState(readSelectedMode);
  const [rival, setRival] = useState("online");
  const [liveMatches, setLiveMatches] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(() => Date.now());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [tournaments, setTournaments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [championship, setChampionship] = useState(null);
  const [joiningChampionship, setJoiningChampionship] = useState(false);
  const [joiningId, setJoiningId] = useState("");
  const [playing, setPlaying] = useState(false);
  const [rankingTab, setRankingTab] = useState("ciencia");
  const [unreadMessages, setUnreadMessages] = useState(0);

  const selectMode = (id) => {
    setSelectedModeId(id);
    try {
      window.localStorage.setItem(SELECTED_MODE_KEY, id);
    } catch {
      // Without storage the choice only lasts while the page is open.
    }
  };

  useEffect(() => {
    let canceled = false;
    const load = () => {
      axios
        .get("/tournaments/live")
        .then((response) => {
          if (canceled) return;
          setLiveMatches(Array.isArray(response?.data) ? response.data : []);
          setFetchedAt(Date.now());
        })
        .catch(() => {});
      axios
        .get("/tournaments")
        .then((response) => {
          if (!canceled) setTournaments(Array.isArray(response?.data) ? response.data : []);
        })
        .catch(() => {});
      axios
        .get("/championship")
        .then((response) => {
          if (!canceled) setChampionship(response?.data || null);
        })
        .catch(() => {});
      axios
        .get("/tournaments/lobby-activity")
        .then((response) => {
          if (!canceled) setActivity(Array.isArray(response?.data) ? response.data : []);
        })
        .catch(() => {});
    };
    load();
    const intervalId = window.setInterval(load, 5000);
    return () => {
      canceled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  // Reloj del turno: el servidor da lo que quedaba al pedir la lista; aquí se descuenta cada segundo.
  useEffect(() => {
    const tickId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(tickId);
  }, []);

  useEffect(() => {
    const handleUnread = (event) =>
      setUnreadMessages(Math.max(0, Number(event?.detail?.total) || 0));
    window.addEventListener(DIRECT_MESSAGE_UNREAD_EVENT, handleUnread);
    setUnreadMessages(Math.max(0, Number(window[DIRECT_MESSAGE_UNREAD_GLOBAL_KEY]?.total) || 0));
    return () => window.removeEventListener(DIRECT_MESSAGE_UNREAD_EVENT, handleUnread);
  }, []);

  const live = useMemo(() => sortLiveMatches(liveMatches), [liveMatches]);
  const replay = useMemo(
    () =>
      liveMatches.find(
        (match) => match?.type === "historical" && match?.hasReplay !== false && match?.board2d
      ) || null,
    [liveMatches]
  );
  const featured = live.length ? live[featuredIndex % live.length] : null;
  const others = live.filter((match) => match !== featured).slice(0, 3);
  const featuredMode = MODE_BY_LIVE[normalizeMode(featured?.mode)];
  const remainingMs = Math.max(
    0,
    (Number(featured?.turnRemainingMs) || 0) - Math.max(0, nowMs - fetchedAt)
  );

  const myIds = useMemo(() => new Set((userIds || []).map(playerKey)), [userIds]);
  const activityRows = useMemo(() => buildActivityRows(activity, myIds), [activity, myIds]);
  const waitingRows = activityRows.filter((row) => row.isWaiting).slice(0, 5);
  // JUGAR first fills a public room of the chosen mode, then an open match without a room yet.
  const joinable = useMemo(
    () => [
      ...activityRows
        .filter((row) => row.canJoin && !row.place?.paid)
        .map((row) => ({
          entry: {
            _id: row.tournamentId,
            mode: row.mode,
            tournamentSequenceNumber: row.place?.number || 0,
          },
        })),
      ...pickWaitingMatches(tournaments, myIds).filter((item) => !item.isMine),
    ],
    [activityRows, myIds, tournaments]
  );

  const selectedMode = MODES.find((mode) => mode.id === selectedModeId) || MODES[0];
  const activeRanking =
    (rankingSections || []).find((section) => section.key === rankingTab) ||
    (rankingSections || [])[0];

  const handlePlay = useCallback(async () => {
    if (rival === "bot") return actions.bot();
    if (rival === "friends") return actions.localFriends();
    if (playing) return undefined;
    setPlaying(true);
    try {
      await onPlayMode(selectedMode.liveMode, joinable);
    } finally {
      setPlaying(false);
    }
    return undefined;
  }, [actions, rival, onPlayMode, playing, selectedMode.liveMode, joinable]);

  const handleJoin = async (entry) => {
    const id = String(entry?._id || "");
    if (!id || joiningId) return;
    setJoiningId(id);
    try {
      await onJoinMatch(entry);
    } finally {
      setJoiningId("");
    }
  };

  const watchMatch = (match) => {
    if (!match?.tournamentId || !match?.roomId) return actions.watch();
    return onNavigate(
      `/game/${match.tournamentId}?autoplay=1&spectator=1&roomId=${encodeURIComponent(match.roomId)}`
    );
  };

  const championshipRow =
    championship && ["registration", "running"].includes(championship.status) ? championship : null;
  const champSeats = championshipRow
    ? `${(championshipRow.players || []).length}/${championshipRow.maxPlayers || 0}`
    : "";
  const champIsOpen = championshipRow?.status === "registration";
  const champIsFull =
    championshipRow && (championshipRow.players || []).length >= (championshipRow.maxPlayers || 0);
  const championshipAction = async () => {
    if (!championshipRow || joiningChampionship) return actions.championship();
    if (championshipRow.joined || championshipRow.hasJoinPassword || !champIsOpen || champIsFull) {
      return actions.championship();
    }
    setJoiningChampionship(true);
    try {
      const response = await axios.post("/championship/join");
      setChampionship(response?.data || championshipRow);
      window.alert(copy.champJoinedOk);
    } catch (error) {
      if (isGuestMustRegisterError(error)) openGuestChampionshipRegister(onNavigate);
      else window.alert(error?.response?.data?.message || copy.champError);
    } finally {
      setJoiningChampionship(false);
    }
    return undefined;
  };
  const champButton = !championshipRow
    ? copy.champWatch
    : !champIsOpen
    ? copy.champWatch
    : championshipRow.joined
    ? copy.champJoined
    : champIsFull
    ? copy.champFull
    : joiningChampionship
    ? "..."
    : `${championshipRow.hasJoinPassword ? "🔒 " : ""}${copy.champJoin}`;

  const playHint =
    rival === "bot"
      ? copy.playBotHint
      : rival === "friends"
      ? copy.playFriendsHint
      : copy.playHint(copy[selectedMode.titleKey]);

  const renderPlayer = (player, index) => {
    if (!player) return <div className="xlv-pcard is-empty" />;
    const isTurn =
      featured?.currentTurnUserId && playerKey(player.userId) === playerKey(featured.currentTurnUserId);
    return (
      <button
        type="button"
        className={`xlv-pcard is-p${index}${isTurn ? " is-turn" : ""}`}
        disabled={Boolean(player.isBot)}
        onClick={() => onOpenPlayerProfile(player)}
      >
        <span className="xlv-avatar">{initialsOf(playerLabel(player))}</span>
        <strong title={playerLabel(player)}>{playerLabel(player)}</strong>
        <span className="xlv-clock">{isTurn ? formatClock(remainingMs) : "··:··"}</span>
        <small>{isTurn ? copy.turn : copy.waitingTurn}</small>
      </button>
    );
  };

  const featuredPlayers = Array.isArray(featured?.players) ? featured.players : [];

  return (
    <div className="xlm-root xlv-root">
      <ModernSidebar
        language={language}
        active="play"
        isAdmin={isAdmin}
        actions={actions}
        badges={{ ...badges, watch: badges.watch ?? live.length, friends: badges.friends ?? socialPending }}
      />

      <div className="xlm-main">
        <header className="xlm-topbar">
          <div className="xlm-heading">
            <span>{copy.greeting(userName)}</span>
            <h1>{copy.title}</h1>
          </div>
          <div className="xlm-design-slot">{designChooser}</div>
          <div className="xlm-top-actions">
            <button type="button" className="xlm-round" onClick={actions.design} aria-label={copy.designMode} title={copy.designMode}>
              <IoBrushOutline />
            </button>
            <button type="button" className="xlm-round" onClick={() => window.dispatchEvent(new CustomEvent(DIRECT_MESSAGE_PANEL_OPEN_EVENT))} aria-label={copy.messages}>
              <IoChatbubbleEllipsesOutline />
              {unreadMessages > 0 ? <b>{Math.min(99, unreadMessages)}</b> : null}
            </button>
            <button type="button" className="xlm-round" onClick={actions.language} aria-label={copy.language} title={copy.language}>
              <IoLanguage />
            </button>
            <button type="button" className="xlm-profile" onClick={onOpenProfile}>
              <span className="xlm-profile-avatar">
                {userAvatar ? <img src={userAvatar} alt="" /> : initialsOf(userName)}
              </span>
              <strong>{userName}</strong>
              <em>
                {rankLabel} · {cimaxTotalLabel}
              </em>
              <IoChevronDown aria-hidden="true" />
            </button>
          </div>
        </header>

        {newsTicker ? <div className="xlm-ticker">{newsTicker}</div> : null}

        <div className="xlv-grid">
          <div className="xlv-col-left">
            <section className="xlv-card xlv-next">
              <h2>🎲 {copy.nextGame}</h2>
              <div className="xlv-label">{copy.mode}</div>
              <div className="xlv-modes">
                {MODES.map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    className={`xlv-mode is-${mode.tone}${mode.id === selectedModeId ? " is-selected" : ""}`}
                    onClick={() => selectMode(mode.id)}
                    aria-pressed={mode.id === selectedModeId}
                  >
                    <span className="xlv-mode-icon">
                      <img src={mode.icon} alt="" />
                    </span>
                    <strong>{copy[mode.titleKey]}</strong>
                    <small>
                      {mode.id === "12" ? "⭐ " : ""}
                      👥 {copy.playing(Number(badges.playingByMode?.[mode.liveMode]) || 0)}
                    </small>
                  </button>
                ))}
              </div>
              <div className="xlv-label">{copy.rival}</div>
              <div className="xlv-seg" role="group" aria-label={copy.rival}>
                {[
                  ["online", "🌍", copy.rivalOnline],
                  ["bot", "🤖", copy.rivalBot],
                  ["friends", "👫", copy.rivalFriends],
                ].map(([id, icon, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={rival === id ? "is-on" : ""}
                    aria-pressed={rival === id}
                    onClick={() => setRival(id)}
                  >
                    <span aria-hidden="true">{icon}</span> {label}
                  </button>
                ))}
              </div>
              <button type="button" className="xlv-play" onClick={handlePlay} disabled={playing}>
                <span className="xlv-play-main">
                  <IoPlay aria-hidden="true" /> {copy.play}
                </span>
                <span className="xlv-play-hint">{playHint}</span>
              </button>
              <div className="xlv-quick">
                <button type="button" onClick={actions.create}>
                  <span aria-hidden="true">➕</span>
                  {copy.create}
                </button>
                <button type="button" onClick={actions.find}>
                  <span aria-hidden="true">🔍</span>
                  {copy.find}
                </button>
                <button type="button" onClick={actions.puzzles}>
                  <span aria-hidden="true">🧩</span>
                  {copy.puzzles}
                </button>
                <button type="button" onClick={actions.sandbox}>
                  <span aria-hidden="true">🏖️</span>
                  {copy.sandbox}
                </button>
              </div>
            </section>

            <section className="xlv-card xlv-learn">
              <h2>🎓 {copy.learnTitle}</h2>
              <p>{copy.learnText}</p>
              <button type="button" className="xlv-learn-main" onClick={onTutorial}>
                ▶ {copy.learnStart}
              </button>
              <button type="button" className="xlv-learn-link" onClick={actions.learn}>
                📖 {copy.learnMore}
              </button>
            </section>
          </div>

          <section className="xlv-card xlv-arena">
            <div className="xlv-arena-head">
              <h2>📺 {copy.liveNow}</h2>
              {live.length ? (
                <span className="xlv-live-pill">
                  <i /> {copy.liveCount(live.length)}
                </span>
              ) : null}
              <button type="button" className="xlv-link" onClick={actions.watch}>
                {copy.seeAll}
              </button>
            </div>

            {featured ? (
              <>
                <div className="xlv-stage">
                  {renderPlayer(featuredPlayers[0], 0)}
                  <div className="xlv-stage-board">
                    <button type="button" className="xlv-board-btn" onClick={() => watchMatch(featured)}>
                      <PuzzleBoard2D board={featured.board2d} size={320} label={copy.boardLabel} />
                    </button>
                    <div className="xlv-stage-meta">
                      <span className={`xlv-mode-chip is-${featuredMode?.tone || "science"}`}>
                        {featuredMode ? copy[featuredMode.titleKey] : featured.mode}
                        {featured.tournamentSequenceNumber ? ` · #${featured.tournamentSequenceNumber}` : ""}
                      </span>
                      <span>
                        {copy.turnN(featured.turnNumber || 1)} · {copy.watchers(Number(featured.spectators) || 0)}
                      </span>
                    </div>
                  </div>
                  {renderPlayer(featuredPlayers[1], 1)}
                </div>
                {featuredPlayers.length > 2 ? (
                  <div className="xlv-more-players">
                    {featuredPlayers.slice(2).map((player, index) => (
                      <span key={`${playerLabel(player)}-${index}`}>{playerLabel(player)}</span>
                    ))}
                  </div>
                ) : null}
                <div className="xlv-stage-actions">
                  <button type="button" className="xlv-watch" onClick={() => watchMatch(featured)}>
                    👁 {copy.watch}
                  </button>
                  {live.length > 1 ? (
                    <button
                      type="button"
                      className="xlv-next-btn"
                      onClick={() => setFeaturedIndex((index) => (index + 1) % live.length)}
                    >
                      ⏭ {copy.next}
                    </button>
                  ) : null}
                </div>
                {others.length ? (
                  <div className="xlv-minis">
                    {others.map((match) => {
                      const mode = MODE_BY_LIVE[normalizeMode(match.mode)];
                      const names = (Array.isArray(match.players) ? match.players : [])
                        .map(playerLabel)
                        .join(" vs ");
                      return (
                        <button
                          key={`${match.tournamentId}-${match.roomId}`}
                          type="button"
                          className="xlv-mini"
                          onClick={() => watchMatch(match)}
                        >
                          <PuzzleBoard2D board={match.board2d} size={150} label={copy.boardLabel} />
                          <span className="xlv-mini-foot">
                            <i className={`is-${mode?.tone || "science"}`} />
                            <strong title={names}>{names}</strong>
                            <em>{copy.watchShort}</em>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="xlv-empty">
                {replay ? (
                  <button type="button" className="xlv-board-btn" onClick={actions.watch}>
                    <PuzzleBoard2D board={replay.board2d} size={260} label={copy.boardLabel} />
                  </button>
                ) : (
                  <span className="xlv-empty-icon" aria-hidden="true">👀</span>
                )}
                <div>
                  {replay ? <span className="xlv-replay-pill">▶ {copy.replay}</span> : null}
                  <strong>{replay ? copy.replayTitle : copy.emptyTitle}</strong>
                  <p>{replay ? copy.replayText : copy.emptyText}</p>
                  {replay ? (
                    <button type="button" className="xlv-watch" onClick={actions.watch}>
                      ▶ {copy.replayButton}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </section>

          <div className="xlv-col-right">
            <section className="xlv-champ">
              <div className="xlv-champ-head">
                <GiPodium aria-hidden="true" />
                <div>
                  <strong>{championshipRow?.title || copy.championship}</strong>
                  <small>
                    {!championshipRow
                      ? copy.champIdle
                      : champIsOpen
                      ? copy.champOpen(champSeats)
                      : copy.champRunning(champSeats)}
                  </small>
                </div>
              </div>
              <button
                type="button"
                className="xlv-champ-btn"
                onClick={championshipAction}
                disabled={joiningChampionship}
              >
                {champButton}
              </button>
            </section>

            <section className="xlv-card">
              <div className="xlv-card-head">
                <h2>🔍 {copy.waitingRival}</h2>
                <button type="button" className="xlv-link" onClick={actions.find}>
                  {copy.seeAll}
                </button>
              </div>
              <div className="xlv-rows">
                {waitingRows.length ? (
                  waitingRows.map((row) => {
                    const mode = MODE_BY_LIVE[normalizeMode(row.mode)];
                    const names = row.players.map((player) => player.displayName).join(", ");
                    return (
                      <div key={`${row.tournamentId}-${row.roomId}`} className={`xlv-row${row.isMine ? " is-mine" : ""}`}>
                        <i className={`is-${mode?.tone || "science"}`} />
                        <span className="xlv-row-name">
                          <strong title={names}>{row.isMine ? copy.mine : names}</strong>
                          <small>
                            {mode ? copy[mode.titleKey] : row.mode} · {copy.minutes(row.havingTime)}
                          </small>
                        </span>
                        {row.isMine ? (
                          <span className="xlv-tag is-mine">{copy.mine}</span>
                        ) : row.canJoin ? (
                          <button
                            type="button"
                            className="xlv-tag is-join"
                            disabled={Boolean(joiningId)}
                            onClick={() =>
                              handleJoin({
                                _id: row.tournamentId,
                                mode: row.mode,
                                tournamentSequenceNumber: row.place?.number || 0,
                              })
                            }
                          >
                            {joiningId === row.tournamentId ? copy.joining : copy.join}
                          </button>
                        ) : (
                          <span className="xlv-tag">{copy.private}</span>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="xlv-rows-empty">
                    <span>{copy.noWaiting}</span>
                    <button type="button" className="xlv-link" onClick={actions.create}>
                      ➕ {copy.createGame}
                    </button>
                  </div>
                )}
              </div>
            </section>

            <section className="xlv-card">
              <div className="xlv-card-head">
                <h2>🏅 {copy.topPlayers}</h2>
                <button type="button" className="xlv-link" onClick={actions.ranking}>
                  {copy.seeAll}
                </button>
              </div>
              <div className="xlv-tabs">
                {(rankingSections || []).map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    className={section.key === activeRanking?.key ? "is-active" : ""}
                    onClick={() => setRankingTab(section.key)}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
              <div className="xlv-top">
                {activeRanking?.players?.length ? (
                  activeRanking.players.slice(0, 5).map((player, index) => (
                    <button
                      key={`${activeRanking.key}-${player.name}-${index}`}
                      type="button"
                      className="xlv-top-row"
                      onClick={() => onOpenPlayerProfile(player.raw)}
                    >
                      <span aria-hidden="true">{["🥇", "🥈", "🥉"][index] || "🏅"}</span>
                      <strong>{player.name}</strong>
                      <em>{player.score}</em>
                    </button>
                  ))
                ) : (
                  <div className="xlv-rows-empty">{rankingLoading ? "…" : copy.noRanking}</div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LobbyLive;
